/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';
import remove from 'lodash/remove';

import findLocalDuplicates from '../import/helpers/findLocalDuplicates';
import calculateTotalSize from '../import/helpers/calculateTotalSize';
import normalizeFiles from './helpers/normalizeFiles';
import findLargeFiles from './helpers/findLargeFiles';

export const addFiles = createAsyncThunk(
  'import/addFiles',
  async (payload, {
    extra: {
      showDialog, showErrorDialog, localization: l10n,
    }, getState,
  }) => {
    const items = payload;

    /** Items normalization */
    const {
      normalizedFiles, corruptedFiles, blackListedFiles, hiddenFiles,
    } = normalizeFiles(items, window.inbox);

    /** Corrupted */
    if (corruptedFiles.length) {
      const { title, text } = l10n.IMPORT.corruptedFiles;
      showErrorDialog(text(corruptedFiles.map(({ name }) => name)), title(corruptedFiles.length));
    }
    /** Blacklisted */
    if (blackListedFiles.length > 0) {
      const { title, text } = l10n.IMPORT.blackListedFiles;
      showErrorDialog(text(blackListedFiles.map((f) => f.name)), title(blackListedFiles.length));
    }
    /** Hidden */
    if (hiddenFiles.length > 0) {
      const { title, text } = l10n.IMPORT.hiddenFiles;
      showErrorDialog(text(hiddenFiles.map((f) => f.name)), title(hiddenFiles.length));
    }

    if (!normalizedFiles.length) return null;
    const filesToAdd = normalizedFiles;

    /** Find local duplicates */
    const localDuplicates = findLocalDuplicates(getState().import.items, filesToAdd);
    if (localDuplicates.length) {
      /** show warning */
      showDialog({
        title: l10n.DIALOGS.IMPORT_WARNING_ADDED_DUPLICATES.TITLE,
        text: l10n.DIALOGS.IMPORT_WARNING_ADDED_DUPLICATES.TEXT(localDuplicates),
        textBtnCancel: null,
        textBtnOk: l10n.DIALOGS.IMPORT_WARNING_ADDED_DUPLICATES.BTN_OK,
      });
      /** Remove local duplicates */
      remove(
        filesToAdd,
        (item) => !!localDuplicates.find(
          (duplicate) => duplicate.name === item.path + item.name && duplicate.size === item.file.size,
        ),
      );
      /** If all are duplicates - exit */
      if (filesToAdd.length < 1) return null;
    }

    const largeFiles = findLargeFiles(filesToAdd);
    if (largeFiles.length) {
      showDialog({
        title: l10n.DIALOGS.IMPORT_WARNING_FILES_EXCEEDS.TITLE,
        text: l10n.DIALOGS.IMPORT_WARNING_FILES_EXCEEDS.TEXT(largeFiles),
        textBtnCancel: null,
        textBtnOk: l10n.DIALOGS.IMPORT_WARNING_FILES_EXCEEDS.BTN_OK,
      });
      remove(
        filesToAdd,
        (file) => !!largeFiles.find(
          (largeFile) => largeFile.name === file.path + file.name && largeFile.size === file.file.size,
        ),
      );
      /** If all are large - exit */
      if (filesToAdd.length < 1) return null;
    }

    return filesToAdd;
  },
);

export const reducer = (builder) => {
  builder
    .addCase(
      addFiles.fulfilled,
      (state, { payload }) => {
        if (!payload) return;

        payload.forEach((item) => {
          const itemToAdd = {
            ...item,
            fields: {
              comment: null,
              title: null,
              description: null,
              keywords: [],
              assignees: [],
              flag: null,
              rating: null,
              color: null,
              customFields: (state.requiredFields?.customFields || []).reduce((r, key) => ({ ...r, [key]: null }), {}),
            },
          };
          if (state.items[item.path]) state.items[item.path].push(itemToAdd);
          else state.items[item.path] = [itemToAdd];
        });

        state.total = calculateTotalSize(state.items);
      },
    );
};
